import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

const groupedMunicipalitiesAtom = atomWithStorage("groupedMunicipalities", []);
const jobsGrowthGroupedMunicipalitiesAtom = atomWithStorage("jobsGrowthGroupedMunicipalities", []);
const imageResponseAtom = atomWithStorage("imageResponse", null);

const municipalitiesAtom = atomWithStorage("municipalities", []);

// Dashboard Filter fields
const selectedMunicipalityAtom = atomWithStorage("selectedMunicipality", null);
const selectedPlaceAtom = atomWithStorage("selectedPlace", null);
const displayMunicNameAtom = atomWithStorage("displayMunicName", null);
const selectedFiltersAtom = atomWithStorage("selectedFilters", { isNationalDisplayed: true, isMetroDisplayed: false, isProvinceDisplayed: false, isSimilarSizeDisplayed: false, baseYear: "2014", startYear: "2014", endYear: "2023", singleYear: "2023" });
const yearsRangeAtom = atomWithStorage("yearsRange", []);
const spatialParameterAtom = atomWithStorage("spatialParameter", null);
const selectedSpatialValueAtom = atomWithStorage("selectedSpatialValue", null);
const selectedSpatialLayerAtom = atomWithStorage("selectedSpatialLayer", null);
const sic7Atom = atomWithStorage("sic7", 0);
const sic7StringAtom = atomWithStorage("sic7String", "");
const growthBySectorIndustryFilterAtom = atomWithStorage("growthBySectorIndustryFilter", "Manufacturing");

// Dashboard Data
const industryStructureDataAtom = atomWithStorage("industryStructureData", []);
const totalJobsGrowthDataAtom = atomWithStorage("totalJobsGrowthData", []);
const newJobsDataAtom = atomWithStorage("newJobsData", []);
const jobsGrowthDataAtom = atomWithStorage("jobsGrowthData", []);
const wageDistributionOverviewDataAtom = atomWithStorage("wageDistributionsData", []);
const growthBySectorDataAtom = atomWithStorage("growthBySectorData", []);
const growthBySectorMainDataAtom = atomWithStorage("growthBySectorMainData", []);
const growthBySectorOtherDataAtom = atomWithStorage("growthBySectorOtherData", []);
const growthBySectorSic7Atom = atomWithStorage("growthBySectorSic7", "-3");
const industryPotentialDataAtom = atomWithStorage("industryPotentialData", []);
const industryPotentialFilterAtom = atomWithStorage("industryPotentialFilter", "All Industries");
const industryPotentialSic7Atom = atomWithStorage("industryPotentialSic7", "");
const industryProfileDataAtom = atomWithStorage("industryProfileData", []);
const industryProfileIndustryTotalAtom = atomWithStorage("industryProfileIndustryTotal", 0);
const exportersDataAtom = atomWithStorage("exportersData", []);
const multiEstablishmentFirmsDataAtom = atomWithStorage("multiEstablishmentFirmsData", []);
const jobDensityMaxValueAtom = atomWithStorage("jobDensityMaxValue", 0);
const jobDensityMinValueAtom = atomWithStorage("jobDensityMinValue", 0);
const jobDensityMapDataAtom = atomWithStorage("jobDensityMapData", null);
const jobDensityMapDataGreyAtom = atomWithStorage("jobDensityMapDataGrey", null);
const jobDensityReportKeyAtom = atomWithStorage("jobDensityReportKey", 1);
const changeInMetroMaxJobsValueAtom = atomWithStorage("changeInMetroMaxJobsValue", 0);
const changeInMetroMinJobsValueAtom = atomWithStorage("changeInMetroMinJobsValue", 0);
const changeInMetroMaxFirmsValueAtom = atomWithStorage("changeInMetroMaxFirmsValue", 0);
const changeInMetroMinFirmsValueAtom = atomWithStorage("changeInMetroMinFirmsValue", 0);
const changeInMetroMapJobsDataAtom = atomWithStorage("changeInMetroMapJobsData", null);
const changeInMetroMapJobsDataGreyAtom = atomWithStorage("changeInMetroMapJobsDataGrey", null);
const changeInMetroMapFirmsDataAtom = atomWithStorage("changeInMetroMapFirmsData", null);
const changeInMetroMapFirmsDataGreyAtom = atomWithStorage("changeInMetroMapFirmsDataGrey", null);
const changeInMetroReportKeyAtom = atomWithStorage("changeInMetroReportKey", 1);
const wageInequalityDataAtom = atomWithStorage("wageInequalityData", []);
const wageLevelsDataAtom = atomWithStorage("wageInequalityData", []);
const jobsForYouthDataAtom = atomWithStorage("jobsForYouthData", []);
const wageDistributionFilterAtom = atomWithStorage("wageDistributionFilter", "All Industries");
const wageDistributionDataAtom = atomWithStorage("wageDistributionData", []);
const wageDistributionUniqueLabelsAtom = atomWithStorage("wageDistributionUniqueLabels", []);
const industriesDataAtom = atomWithStorage("industriesData", []);

// Small Business dashboards (added 2026-04-07)
// NOTE: smallBusinessMapIntensityDataAtom uses plain atom (memory-only), not atomWithStorage,
// because the polygon data for district/province/local-muni layers contains the full SA boundary
// geometry (multi-MB JSON) which exceeds the localStorage ~5MB quota. The map can be re-fetched
// on page reload — no need to persist.
const smallBusinessMapIntensityDataAtom = atom([]);
const smallBusinessMapChangeDataAtom = atom([]); // Same pattern as Intensity — memory-only, polygon data too large for localStorage
const smallBusinessMapChangeModeAtom = atomWithStorage("smallBusinessMapChangeMode", "Absolute Change"); // "Absolute Change" or "Percentage Change"
const smallBusinessJobsGrowthDataAtom = atomWithStorage("smallBusinessJobsGrowthData", []);
const smallBusinessJobsGrowthGroupedAtom = atomWithStorage("smallBusinessJobsGrowthGrouped", []);
const establishmentsVsEmploymentDataAtom = atomWithStorage("establishmentsVsEmploymentData", []);
const smallBusinessSectorTrendsDataAtom = atomWithStorage("smallBusinessSectorTrendsData", []);
const smallBusinessSectorAtom = atomWithStorage("smallBusinessSector", "-3"); // mirrors growthBySectorSic7Atom — default Manufacturing

// firmSizeRangeAtom — single keyed object for the FirmSizeRangeSlider, one slot per dashboard.
// Values are actual FirmSizeFTE bin edges (not slider indices) so they survive any future bin changes.
// Defaults match plan §5: small=0–49 default for dashboards #1, #2; granular 5-bucket for #3; 3-bucket for #5.
const firmSizeRangeAtom = atomWithStorage("firmSizeRange", {
  smallBusinessMapIntensity: { handles: [50] },
  smallBusinessJobsGrowth: { handles: [50] },
  establishmentsVsEmployment: { handles: [10, 50, 250, 1000] },
  smallBusinessSectorTrends: { handles: [10, 50, 250, 1000] },
});

// Data Config — fetched from /api/data-config/ on app load. Holds range edges,
// enum options, and other data-dependent config from the DB. null until fetched.
const dataConfigAtom = atom(null);

// Small Business Map of Intensity — zoom & highlight toggles (B5.8 / B5.9). Persisted so
// the user's choice is sticky across spatial layer changes and page reloads.
const smallBusinessMapZoomInAtom = atomWithStorage("smallBusinessMapZoomIn", false);
const smallBusinessMapHighlightAtom = atomWithStorage("smallBusinessMapHighlight", true);

// Small Business Maps — Colour toggle (2nd revert R5). Per-layer independent: each spatial
// layer enters with its own default (mono at Province/District, colour at Local Muni/Metro).
// User overrides apply only to the current layer and do not survive a layer switch; the main
// map component resets this atom to the layer default on every layerConfig.mode change.
// Plain in-memory atom (NOT atomWithStorage) — persistence would fight the per-layer reset.
const smallBusinessMapColourOnAtom = atom(false);

// UI State
const dataNoticeCollapsedAtom = atomWithStorage("dataNoticeCollapsed", false);

export {
  groupedMunicipalitiesAtom,
  jobsGrowthGroupedMunicipalitiesAtom,
  imageResponseAtom,
  selectedMunicipalityAtom,
  displayMunicNameAtom,
  selectedFiltersAtom,
  totalJobsGrowthDataAtom,
  newJobsDataAtom,
  industryStructureDataAtom,
  yearsRangeAtom,
  jobsGrowthDataAtom,
  wageDistributionOverviewDataAtom,
  growthBySectorMainDataAtom,
  growthBySectorOtherDataAtom,
  growthBySectorIndustryFilterAtom,
  growthBySectorDataAtom,
  growthBySectorSic7Atom,
  industryProfileDataAtom,
  industryProfileIndustryTotalAtom,
  industryPotentialDataAtom,
  industryPotentialFilterAtom,
  industryPotentialSic7Atom,
  exportersDataAtom,
  multiEstablishmentFirmsDataAtom,
  jobDensityMaxValueAtom,
  jobDensityMinValueAtom,
  jobDensityMapDataAtom,
  jobDensityMapDataGreyAtom,
  jobDensityReportKeyAtom,
  wageInequalityDataAtom,
  wageLevelsDataAtom,
  jobsForYouthDataAtom,
  wageDistributionDataAtom,
  wageDistributionFilterAtom,
  wageDistributionUniqueLabelsAtom,
  selectedPlaceAtom,
  spatialParameterAtom,
  selectedSpatialValueAtom,
  selectedSpatialLayerAtom,
  sic7Atom,
  sic7StringAtom,
  industriesDataAtom,
  municipalitiesAtom,
  changeInMetroMaxJobsValueAtom,
  changeInMetroMinJobsValueAtom,
  changeInMetroMaxFirmsValueAtom,
  changeInMetroMinFirmsValueAtom,
  changeInMetroMapJobsDataAtom,
  changeInMetroMapJobsDataGreyAtom,
  changeInMetroMapFirmsDataAtom,
  changeInMetroMapFirmsDataGreyAtom,
  changeInMetroReportKeyAtom,
  smallBusinessMapIntensityDataAtom,
  smallBusinessMapChangeDataAtom,
  smallBusinessMapChangeModeAtom,
  smallBusinessJobsGrowthDataAtom,
  smallBusinessJobsGrowthGroupedAtom,
  establishmentsVsEmploymentDataAtom,
  smallBusinessSectorTrendsDataAtom,
  smallBusinessSectorAtom,
  firmSizeRangeAtom,
  smallBusinessMapZoomInAtom,
  smallBusinessMapHighlightAtom,
  smallBusinessMapColourOnAtom,
  dataConfigAtom,
  dataNoticeCollapsedAtom,
};
